import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns'

import type { AttendancePeriod, AttendanceSegment } from './types'

export const buildAttendancePeriods = (
  segments: ReadonlyArray<AttendanceSegment>,
  periodStart: string,
  periodEnd: string
): AttendancePeriod[] => {
  if (segments.length === 0) return []

  const sortedSegments = [...segments].sort((a, b) => a.date.localeCompare(b.date))
  const firstSegment = sortedSegments[0]
  const lastSegment = sortedSegments[sortedSegments.length - 1]
  if (!firstSegment || !lastSegment) return []

  const fallbackStartDate = parseISO(firstSegment.date)
  const fallbackEndDate = parseISO(lastSegment.date)
  const parsedPeriodStartDate = parseISO(periodStart)
  const parsedPeriodEndDate = parseISO(periodEnd)
  const periodStartDate = Number.isNaN(parsedPeriodStartDate.getTime())
    ? fallbackStartDate
    : parsedPeriodStartDate
  const resolvedPeriodEndDate = Number.isNaN(parsedPeriodEndDate.getTime())
    ? fallbackEndDate
    : parsedPeriodEndDate
  const periodEndDate =
    resolvedPeriodEndDate.getTime() < periodStartDate.getTime()
      ? periodStartDate
      : resolvedPeriodEndDate

  const periodsByWeekIndex = sortedSegments.reduce<Map<number, AttendancePeriod>>(
    (periods, segment) => {
      const segmentDate = parseISO(segment.date)
      const weekIndex = Math.max(
        0,
        Math.floor(differenceInCalendarDays(segmentDate, periodStartDate) / 7)
      )
      const existingPeriod = periods.get(weekIndex)

      if (existingPeriod) {
        periods.set(weekIndex, {
          ...existingPeriod,
          workedMinutes: existingPeriod.workedMinutes + segment.workedMinutes,
          paidLeaveMinutes: existingPeriod.paidLeaveMinutes + segment.paidLeaveMinutes,
          bankHolidayCreditMinutes:
            existingPeriod.bankHolidayCreditMinutes + segment.bankHolidayCreditMinutes,
          premiumBonusMinutes: existingPeriod.premiumBonusMinutes + segment.premiumBonusMinutes,
          totalPayableMinutes: existingPeriod.totalPayableMinutes + segment.totalPayableMinutes,
          approvedDaysCount: existingPeriod.approvedDaysCount + 1,
        })
        return periods
      }

      const weekStartDate = addDays(periodStartDate, weekIndex * 7)
      const rawWeekEndDate = addDays(weekStartDate, 6)
      const weekEndDate =
        rawWeekEndDate.getTime() > periodEndDate.getTime() ? periodEndDate : rawWeekEndDate

      periods.set(weekIndex, {
        startDate: format(weekStartDate, 'yyyy-MM-dd'),
        endDate: format(weekEndDate, 'yyyy-MM-dd'),
        workedMinutes: segment.workedMinutes,
        paidLeaveMinutes: segment.paidLeaveMinutes,
        bankHolidayCreditMinutes: segment.bankHolidayCreditMinutes,
        premiumBonusMinutes: segment.premiumBonusMinutes,
        totalPayableMinutes: segment.totalPayableMinutes,
        approvedDaysCount: 1,
      })

      return periods
    },
    new Map<number, AttendancePeriod>()
  )

  return [...periodsByWeekIndex.entries()]
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
    .map(([, period]) => period)
}
